import { ref, onMounted, watch, computed, reactive, nextTick } from 'vue'
import { nanoid } from 'nanoid'
import api from '@/utils/ky'
import { confirm } from '@/utils/events'

function getDefaultPage ({ lang, country, isMainPage }) {
  return {
    id: 'new_' + nanoid(),
    title: '',
    lang,
    country,
    blocks: [],
    isMainPage
  }
}

const fetchedPages = ref([])
const pages = ref([])

export function usePages ({ router, route } = {}) {
  function fetchPages () {
    const isFetchedPages = ref(false)

    onMounted(async () => {
      const { data } = await api.get('admin/v2/articles/pages')

      fetchedPages.value = data
      isFetchedPages.value = true
    })

    return { isFetchedPages }
  }

  function getPages (pagesList = fetchedPages.value) {
    return structuredClone(
      pagesList.filter(
        page =>
          page.lang === route?.params?.lang &&
          page.country === route?.params?.country
      )
    )
  }

  function getPageById (id) {
    return pages.value.find(page => page.id === id)
  }

  function addPage ({ lang }) {
    const page = getDefaultPage({
      lang: route.params.lang,
      country: route.params.country,
      isMainPage: pages.value.length === 0
    })
    pages.value.push(page)

    router.push({
      name: 'articles-page-item',
      params: {
        id: page.id
      }
    })
  }

  async function createPage (rawPage) {
    const page = structuredClone(rawPage)

    delete page.id

    const { data } = await api.post('admin/v2/articles/pages', page)
    fetchedPages.value = data
    pages.value = []
    await nextTick()
    pages.value = getPages(data)

    if (route.name !== 'articles-pages') {
      router.push({ name: 'articles-pages' })
    }
  }

  async function updatePage (rawPage) {
    const page = structuredClone(rawPage)

    const { data } = await api.patch(`admin/v2/articles/pages/${page.id}`, page)
    fetchedPages.value = data
    pages.value = []
    await nextTick()
    pages.value = getPages(data)

    if (route.name !== 'articles-pages') {
      router.push({ name: 'articles-pages' })
    }
  }

  async function deletePage (page) {
    const confirmed = await confirm('Точно удаляем?')
    if (!confirmed) return

    await api.delete(`admin/v2/articles/pages/${page.id}`)

    const fetchedPageIndex = fetchedPages.value.findIndex(p => p.id === page.id)
    if (fetchedPageIndex > -1) {
      fetchedPages.value.splice(fetchedPageIndex, 1)
    }

    const pagesIndex = pages.value.findIndex(p => Object.is(p, page))
    pages.value.splice(pagesIndex, 1)

    if (route.name !== 'articles-pages') {
      router.push({ name: 'articles-pages' })
    }
  }

  const mainPage = computed(() => {
    return pages.value.find(page => page.isMainPage === true)
  })

  function changeMainPage (page, val) {
    if (!val) return

    pages.value.forEach(p => {
      p.isMainPage = false
    })

    page.isMainPage = true
  }

  async function savePages () {
    for (const page of pages.value) {
      if (page.id.startsWith('new_')) delete page.id
    }

    const { data } = await api.put('admin/v2/articles/pages', pages.value)
    fetchedPages.value = data
    pages.value = []
    await nextTick()
    pages.value = getPages(data)
  }

  return {
    pages,
    fetchPages,
    getPages,
    getPageById,
    addPage,
    createPage,
    updatePage,
    deletePage,
    mainPage,
    changeMainPage,
    savePages
  }
}
